import React, { useEffect, useState } from "react";
import "./TailoringOrderDetail.css";
import { backend_url } from "../../App";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TABS = ["Order Info", "Design", "Production", "Embellishment", "Quality Control", "Payment", "Delivery"];

const STAGE_LABELS = [
  ["patternMaking", "Pattern Making"], ["fabricCutting", "Fabric Cutting"], ["stitching", "Stitching"],
  ["trial1", "Trial 1"], ["alteration", "Alteration"], ["trial2", "Trial 2"],
  ["finishing", "Finishing"], ["ironing", "Ironing"], ["packing", "Packing"],
];

const EMBELLISHMENT_OPTIONS = ["Aari Work", "Kundan Work", "Zardosi Work", "Hand Embroidery", "Stone Work", "Mirror Work", "Bead Work"];

const QC_FIELDS = [
  ["measurementVerified", "Measurement Verification"],
  ["stitchQualityChecked", "Stitch Quality Check"],
  ["fabricDefectChecked", "Fabric Defect Check"],
  ["embroideryChecked", "Embroidery Check"],
  ["finishingApproved", "Finishing Approval"],
];

const TailoringOrderDetail = ({ orderId, onClose, onSaved }) => {
  const [activeTab, setActiveTab] = useState("Order Info");
  const [order, setOrder] = useState(null);
  const [fabrics, setFabrics] = useState([]);
  const [printDesigns, setPrintDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [labourForm, setLabourForm] = useState({ workerName: "", workType: "", hours: "", cost: "" });
  const [paymentForm, setPaymentForm] = useState({ amount: "", method: "Cash", note: "" });

  useEffect(() => {
    fetchOrder();
    fetch(`${backend_url}/api/fabrics`).then((r) => r.json()).then((d) => { if (d.success) setFabrics(d.fabrics); }).catch(() => {});
    fetch(`${backend_url}/api/print-designs`).then((r) => r.json()).then((d) => { if (d.success) setPrintDesigns(d.designs); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backend_url}/api/tailoring-orders/${orderId}`);
      const data = await res.json();
      if (!data.success) throw new Error();
      setOrder(data.order);
    } catch {
      toast.error("Failed to load order");
    } finally {
      setLoading(false);
    }
  };

  const patch = async (path, body, successMsg) => {
    setSaving(true);
    try {
      const res = await fetch(`${backend_url}/api/tailoring-orders/${orderId}${path}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error();
      setOrder(data.order);
      onSaved && onSaved();
      if (successMsg) toast.success(successMsg);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const post = async (path, body, successMsg, isForm = false) => {
    setSaving(true);
    try {
      const res = await fetch(`${backend_url}/api/tailoring-orders/${orderId}${path}`, {
        method: "POST",
        headers: isForm ? undefined : { "Content-Type": "application/json" },
        body: isForm ? body : JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error();
      setOrder(data.order);
      onSaved && onSaved();
      if (successMsg) toast.success(successMsg);
      return data;
    } catch {
      toast.error("Failed to save");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const uploadDesignImages = async (type, files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("images", f));
    await post(`/design/images/${type}`, fd, "Images added", true);
  };

  const uploadSignature = async (file) => {
    const fd = new FormData();
    fd.append("signature", file);
    await post("/delivery/signature", fd, "Signature saved", true);
  };

  const paymentTotal = order ? (order.payment.advancePaid || 0) + (order.payment.paymentHistory || []).reduce((s, p) => s + (p.amount || 0), 0) : 0;
  const pendingAmount = order ? Math.max(0, (order.payment.quotationAmount || 0) - paymentTotal) : 0;

  if (loading || !order) {
    return (
      <div className="tod-overlay"><div className="tod-modal"><p className="tod-loading">Loading…</p></div></div>
    );
  }

  return (
    <>
      <div className="tod-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="tod-modal">
          <button className="tod-close" onClick={onClose}>✕</button>
          <div className="tod-header">
            <h2>{order.orderId}</h2>
            <span className="tod-customer-chip">{order.customerId?.name}</span>
          </div>

          <div className="tod-tabs">
            {TABS.map((t) => (
              <button key={t} className={`tod-tab ${activeTab === t ? "active" : ""}`} onClick={() => setActiveTab(t)}>{t}</button>
            ))}
          </div>

          <div className="tod-body">
            {/* ── Order Info ── */}
            {activeTab === "Order Info" && (
              <div className="tod-grid">
                <label className="tod-field"><span>Order Type</span>
                  <select value={order.orderType} onChange={(e) => patch("", { orderType: e.target.value })}>
                    {["Fabric Order", "Tailoring Order", "Designer Wear Order", "Bulk Order"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
                <label className="tod-field"><span>Occasion</span>
                  <select value={order.occasion} onChange={(e) => patch("", { occasion: e.target.value })}>
                    {["Wedding", "Party", "Casual", "Festival", "Other"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
                <label className="tod-field"><span>Priority Level</span>
                  <select value={order.priorityLevel} onChange={(e) => patch("", { priorityLevel: e.target.value })}>
                    {["Low", "Medium", "High", "Urgent"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
                <label className="tod-field"><span>Status</span>
                  <select value={order.status} onChange={(e) => patch("", { status: e.target.value })}>
                    {["New", "In Progress", "Completed", "Delivered", "Cancelled"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
                <label className="tod-field"><span>Delivery Date</span>
                  <input type="date" defaultValue={order.deliveryDate ? order.deliveryDate.slice(0, 10) : ""}
                    onBlur={(e) => patch("", { deliveryDate: e.target.value })} />
                </label>
                <label className="tod-field"><span>Fabric Used</span>
                  <select value={order.fabricUsed?._id || ""} onChange={(e) => patch("", { fabricUsed: e.target.value })}>
                    <option value="">— none —</option>
                    {fabrics.map((f) => <option key={f._id} value={f._id}>{f.fabricType} ({f.fabricId})</option>)}
                  </select>
                </label>
                <label className="tod-field"><span>Fabric Quantity Used</span>
                  <input type="number" defaultValue={order.fabricQuantityUsed || ""} onBlur={(e) => patch("", { fabricQuantityUsed: e.target.value })} />
                </label>
                <label className="tod-field"><span>Print Design Used</span>
                  <select value={order.printDesignUsed?._id || ""} onChange={(e) => patch("", { printDesignUsed: e.target.value })}>
                    <option value="">— none —</option>
                    {printDesigns.map((d) => <option key={d._id} value={d._id}>{d.floralCollection || d.printDesignId} ({d.printDesignId})</option>)}
                  </select>
                </label>
              </div>
            )}

            {/* ── Design ── */}
            {activeTab === "Design" && (
              <div className="tod-design">
                <div className="tod-checkbox-row">
                  <label><input type="checkbox" checked={order.design.patternMasterApproved} onChange={(e) => patch("/design", { patternMasterApproved: e.target.checked })} /> Pattern Master Approval</label>
                  <label><input type="checkbox" checked={order.design.customerApproved} onChange={(e) => patch("/design", { customerApproved: e.target.checked })} /> Customer Approval</label>
                </div>

                {[["sketch", "Design Sketches", order.design.sketchImages], ["cad", "CAD Designs", order.design.cadImages], ["reference", "Reference Images", order.design.referenceImages]].map(([type, label, imgs]) => (
                  <div key={type} className="tod-image-section">
                    <span className="tod-image-label">{label}</span>
                    <div className="tod-image-row">
                      {imgs.map((url) => <img key={url} src={url} alt={label} className="tod-image-thumb" />)}
                      <label className="tod-image-upload">
                        + Add
                        <input type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files.length && uploadDesignImages(type, e.target.files)} />
                      </label>
                    </div>
                  </div>
                ))}

                <div className="tod-revision-section">
                  <span className="tod-image-label">Revision History</span>
                  {order.design.revisionHistory.length === 0 ? (
                    <p className="tod-empty">No revisions logged yet.</p>
                  ) : (
                    <ul className="tod-revision-list">
                      {order.design.revisionHistory.map((r, i) => (
                        <li key={i}><span>{new Date(r.date).toLocaleDateString()}</span> — {r.note}</li>
                      ))}
                    </ul>
                  )}
                  <div className="tod-inline-add">
                    <input type="text" placeholder="Add revision note…" value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)} />
                    <button onClick={async () => { if (!revisionNote.trim()) return; await post("/design/revisions", { note: revisionNote }); setRevisionNote(""); }}>Add</button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Production ── */}
            {activeTab === "Production" && (
              <div className="tod-stages">
                {STAGE_LABELS.map(([key, label]) => {
                  const stage = order.production[key] || {};
                  return (
                    <div key={key} className="tod-stage-row">
                      <label className="tod-stage-check">
                        <input type="checkbox" checked={!!stage.done}
                          onChange={(e) => patch("/production", { [key]: { done: e.target.checked } })} />
                        {label}
                      </label>
                      <input type="date" className="tod-stage-date" defaultValue={stage.date ? stage.date.slice(0, 10) : ""}
                        onBlur={(e) => patch("/production", { [key]: { date: e.target.value } })} />
                      <input type="text" className="tod-stage-notes" placeholder="Notes…" defaultValue={stage.notes || ""}
                        onBlur={(e) => patch("/production", { [key]: { notes: e.target.value } })} />
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Embellishment ── */}
            {activeTab === "Embellishment" && (
              <div className="tod-embellishment">
                <span className="tod-image-label">Work Types</span>
                <div className="tod-checkbox-grid">
                  {EMBELLISHMENT_OPTIONS.map((opt) => (
                    <label key={opt}>
                      <input type="checkbox" checked={order.embellishment.types.includes(opt)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...order.embellishment.types, opt]
                            : order.embellishment.types.filter((t) => t !== opt);
                          patch("/embellishment", { types: next });
                        }} />
                      {opt}
                    </label>
                  ))}
                </div>

                <span className="tod-image-label" style={{ marginTop: 18, display: "block" }}>Labour Tracking</span>
                {order.embellishment.labourTracking.length === 0 ? (
                  <p className="tod-empty">No labour entries yet.</p>
                ) : (
                  <table className="tod-mini-table">
                    <thead><tr><th>Worker</th><th>Work</th><th>Hours</th><th>Cost</th><th>Date</th></tr></thead>
                    <tbody>
                      {order.embellishment.labourTracking.map((l, i) => (
                        <tr key={i}><td>{l.workerName}</td><td>{l.workType}</td><td>{l.hours}</td><td>₹{l.cost}</td><td>{new Date(l.date).toLocaleDateString()}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="tod-labour-form">
                  <input placeholder="Worker name" value={labourForm.workerName} onChange={(e) => setLabourForm((p) => ({ ...p, workerName: e.target.value }))} />
                  <input placeholder="Work type" value={labourForm.workType} onChange={(e) => setLabourForm((p) => ({ ...p, workType: e.target.value }))} />
                  <input placeholder="Hours" type="number" value={labourForm.hours} onChange={(e) => setLabourForm((p) => ({ ...p, hours: e.target.value }))} />
                  <input placeholder="Cost ₹" type="number" value={labourForm.cost} onChange={(e) => setLabourForm((p) => ({ ...p, cost: e.target.value }))} />
                  <button onClick={async () => {
                    if (!labourForm.workerName) return;
                    await post("/embellishment/labour", labourForm);
                    setLabourForm({ workerName: "", workType: "", hours: "", cost: "" });
                  }}>Add</button>
                </div>
              </div>
            )}

            {/* ── Quality Control ── */}
            {activeTab === "Quality Control" && (
              <div className="tod-qc">
                {QC_FIELDS.map(([key, label]) => (
                  <label key={key} className="tod-qc-row">
                    <input type="checkbox" checked={order.qualityControl[key]} onChange={(e) => patch("/quality-control", { [key]: e.target.checked })} />
                    {label}
                  </label>
                ))}
                <label className="tod-field" style={{ marginTop: 12 }}>
                  <span>Final QC Status</span>
                  <select value={order.qualityControl.finalStatus} onChange={(e) => patch("/quality-control", { finalStatus: e.target.value })}>
                    {["Pending", "Passed", "Failed"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
              </div>
            )}

            {/* ── Payment ── */}
            {activeTab === "Payment" && (
              <div className="tod-payment">
                <div className="tod-grid">
                  <label className="tod-field"><span>Quotation Amount (₹)</span>
                    <input type="number" defaultValue={order.payment.quotationAmount} onBlur={(e) => patch("/payment", { quotationAmount: e.target.value })} />
                  </label>
                  <label className="tod-field"><span>Advance Paid (₹)</span>
                    <input type="number" defaultValue={order.payment.advancePaid} onBlur={(e) => patch("/payment", { advancePaid: e.target.value })} />
                  </label>
                  <label className="tod-field"><span>Invoice Number</span>
                    <input type="text" defaultValue={order.payment.invoiceNumber} onBlur={(e) => patch("/payment", { invoiceNumber: e.target.value })} />
                  </label>
                  <label className="tod-field"><span>GST Rate (%)</span>
                    <input type="number" defaultValue={order.payment.gstRate} onBlur={(e) => patch("/payment", { gstRate: e.target.value })} />
                  </label>
                  <label className="tod-field"><span>GST Amount (₹)</span>
                    <input type="number" defaultValue={order.payment.gstAmount} onBlur={(e) => patch("/payment", { gstAmount: e.target.value })} />
                  </label>
                </div>

                <div className="tod-payment-summary">
                  <div><span>Total Received</span><strong>₹{paymentTotal}</strong></div>
                  <div><span>Pending / Balance</span><strong>₹{pendingAmount}</strong></div>
                </div>

                <span className="tod-image-label">Payment History</span>
                {order.payment.paymentHistory.length === 0 ? (
                  <p className="tod-empty">No payments logged yet.</p>
                ) : (
                  <table className="tod-mini-table">
                    <thead><tr><th>Amount</th><th>Method</th><th>Note</th><th>Date</th></tr></thead>
                    <tbody>
                      {order.payment.paymentHistory.map((p, i) => (
                        <tr key={i}><td>₹{p.amount}</td><td>{p.method}</td><td>{p.note}</td><td>{new Date(p.date).toLocaleDateString()}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="tod-labour-form">
                  <input placeholder="Amount ₹" type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))} />
                  <select value={paymentForm.method} onChange={(e) => setPaymentForm((p) => ({ ...p, method: e.target.value }))}>
                    {["Cash", "UPI", "Card", "Bank Transfer"].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <input placeholder="Note" value={paymentForm.note} onChange={(e) => setPaymentForm((p) => ({ ...p, note: e.target.value }))} />
                  <button onClick={async () => {
                    if (!paymentForm.amount) return;
                    await post("/payment/history", paymentForm);
                    setPaymentForm({ amount: "", method: "Cash", note: "" });
                  }}>Add</button>
                </div>
              </div>
            )}

            {/* ── Delivery ── */}
            {activeTab === "Delivery" && (
              <div className="tod-grid">
                <label className="tod-field"><span>Method</span>
                  <select value={order.delivery.method} onChange={(e) => patch("/delivery", { method: e.target.value })}>
                    {["Pickup", "Courier"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
                <label className="tod-field"><span>Courier Name</span>
                  <input type="text" defaultValue={order.delivery.courierName} onBlur={(e) => patch("/delivery", { courierName: e.target.value })} />
                </label>
                <label className="tod-field"><span>Tracking Number</span>
                  <input type="text" defaultValue={order.delivery.trackingNumber} onBlur={(e) => patch("/delivery", { trackingNumber: e.target.value })} />
                </label>
                <label className="tod-field"><span>Delivered On</span>
                  <input type="date" defaultValue={order.delivery.deliveredAt ? order.delivery.deliveredAt.slice(0, 10) : ""} onBlur={(e) => patch("/delivery", { deliveredAt: e.target.value })} />
                </label>
                <label className="tod-field tod-checkbox-inline">
                  <input type="checkbox" checked={order.delivery.confirmed} onChange={(e) => patch("/delivery", { confirmed: e.target.checked })} />
                  <span>Delivery Confirmed (customer signed off)</span>
                </label>

                <div className="tod-image-section" style={{ gridColumn: "1 / -1" }}>
                  <span className="tod-image-label">Customer Signature</span>
                  <div className="tod-image-row">
                    {order.delivery.customerSignatureImage && (
                      <img src={order.delivery.customerSignatureImage} alt="Customer signature" className="tod-image-thumb" />
                    )}
                    <label className="tod-image-upload">
                      {order.delivery.customerSignatureImage ? "Replace" : "+ Add"}
                      <input type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadSignature(e.target.files[0])} />
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={2000} />
    </>
  );
};

export default TailoringOrderDetail;
